import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Users, Target, Award, Compass, Rocket, Gem } from "lucide-react";
import AdminEditWrapper from "../components/AdminEditWrapper";

export default function ChiSiamo() {
  const { isAdmin } = useAuth();
  const [chiSiamoContent, setChiSiamoContent] = useState([]);
  const [visioneContent, setVisioneContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", content: "" });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const chiSiamoRes = await axios.get(`${API}/content/chi-siamo`);
      const allContent = chiSiamoRes.data;
      // Split: first 2 items are "chi siamo", rest are "visione/obiettivi"
      setChiSiamoContent(allContent.filter((_, i) => i < 2));
      setVisioneContent(allContent.filter((_, i) => i >= 2));
    } catch (e) {
      toast.error("Errore nel caricamento dei contenuti");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ title: item.title || "", content: item.content });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", content: "" });
  };

  const saveEdit = async (id) => {
    try {
      await axios.put(`${API}/content/${id}`, editForm);
      toast.success("Contenuto aggiornato");
      fetchContent();
      cancelEdit();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const addContent = async (section) => {
    try {
      const allContent = [...chiSiamoContent, ...visioneContent];
      await axios.post(`${API}/content`, {
        section: "chi-siamo",
        content_type: "text",
        title: section === "chi-siamo" ? "Nuovo Titolo" : "Nuovo Obiettivo",
        content: "Inserisci il contenuto qui...",
        order: allContent.length
      });
      toast.success("Contenuto aggiunto");
      fetchContent();
    } catch (e) {
      toast.error("Errore nell'aggiunta");
    }
  };

  const deleteContent = async (id) => {
    try {
      await axios.delete(`${API}/content/${id}`);
      toast.success("Contenuto eliminato");
      fetchContent();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const chiSiamoIcons = [Users, Target, Award];
  const visioneIcons = [Compass, Rocket, Gem];

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center" data-testid="chi-siamo-loading">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="chi-siamo-page">
      <PageHero image={HERO_IMAGES.chisiamo} kicker="La nostra storia" title="Chi Siamo" subtitle="La nostra storia, i nostri valori e la nostra visione" />
      <SectionBg type="gold" />
      <div className="container py-8 relative">

        {/* Chi Siamo Section */}
        <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8 mb-16 sm:mb-20">
          {chiSiamoContent.map((item, index) => {
            const Icon = chiSiamoIcons[index % chiSiamoIcons.length];
            return (
              <AdminEditWrapper
                key={item.id}
                isAdmin={isAdmin}
                isEditing={editingId === item.id}
                onEdit={() => startEdit(item)}
                onSave={() => saveEdit(item.id)}
                onCancel={cancelEdit}
                onDelete={() => deleteContent(item.id)}
                showDelete={true}
              >
                <div 
                  className={`glass-card p-6 sm:p-8 md:p-10 animate-fadeIn`}
                  data-testid={`chi-siamo-content-${index}`}
                >
                  {editingId === item.id ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="admin-input text-xl font-semibold"
                        placeholder="Titolo"
                        data-testid="edit-chi-siamo-title"
                      />
                      <textarea
                        value={editForm.content}
                        onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                        className="admin-textarea"
                        placeholder="Contenuto"
                        rows={6}
                        data-testid="edit-chi-siamo-content"
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
                      <div 
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: "rgba(47, 216, 168, 0.15)" }}
                      >
                        <Icon size={30} style={{ color: "var(--mm-gold)" }} />
                      </div>
                      <div>
                        <h2 
                          className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4"
                          style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}
                        >
                          {item.title}
                        </h2>
                        <p 
                          className="text-base sm:text-lg leading-relaxed"
                          style={{ color: "var(--mm-text-secondary)" }}
                        >
                          {item.content}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </AdminEditWrapper>
            );
          })}

          {/* Admin Add Chi Siamo Button */}
          {isAdmin && (
            <div className="flex justify-center">
              <button
                onClick={() => addContent("chi-siamo")}
                className="btn-secondary flex items-center gap-2"
                data-testid="add-chi-siamo-btn"
              >
                + Aggiungi Sezione
              </button>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 max-w-4xl mx-auto mb-12 sm:mb-16">
          <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
          <div 
            className="w-3 h-3 rotate-45"
            style={{ backgroundColor: "var(--mm-gold)" }}
          />
          <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
        </div>

        {/* Visione e Obiettivi Section */}
        <div className="max-w-4xl mx-auto">
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-10 text-center gold-text"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            VISIONE E OBIETTIVI
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {visioneContent.map((item, index) => {
              const Icon = visioneIcons[index % visioneIcons.length];
              return (
                <AdminEditWrapper
                  key={item.id}
                  isAdmin={isAdmin}
                  isEditing={editingId === item.id}
                  onEdit={() => startEdit(item)}
                  onSave={() => saveEdit(item.id)}
                  onCancel={cancelEdit}
                  onDelete={() => deleteContent(item.id)}
                  showDelete={true}
                >
                  <div 
                    className={`glass-card p-5 sm:p-6 h-full animate-fadeIn hover:scale-[1.02] transition-transform duration-300`}
                    style={{ borderColor: "rgba(47, 216, 168, 0.2)" }}
                    data-testid={`visione-card-${index}`}
                  >
                    {editingId === item.id ? (
                      <div className="space-y-4">
                        <input
                          type="text"
                          value={editForm.title}
                          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          className="admin-input text-xl font-semibold"
                          placeholder="Titolo"
                          data-testid="edit-visione-title"
                        />
                        <textarea
                          value={editForm.content}
                          onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                          className="admin-textarea"
                          placeholder="Contenuto"
                          rows={4}
                          data-testid="edit-visione-content"
                        />
                      </div>
                    ) : (
                      <>
                        <div 
                          className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mb-4"
                          style={{ backgroundColor: "rgba(47, 216, 168, 0.15)" }}
                        >
                          <Icon size={26} style={{ color: "var(--mm-gold)" }} />
                        </div>
                        <h3 
                          className="text-lg sm:text-xl md:text-2xl font-bold mb-3 gold-text"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {item.title}
                        </h3>
                        <p 
                          className="text-sm sm:text-base leading-relaxed"
                          style={{ color: "var(--mm-text-secondary)" }}
                        >
                          {item.content}
                        </p>
                      </>
                    )}
                  </div>
                </AdminEditWrapper>
              );
            })}
          </div>

          {/* Admin Add Visione Button */}
          {isAdmin && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => addContent("visione")}
                className="btn-secondary flex items-center gap-2"
                data-testid="add-visione-btn"
              >
                + Aggiungi Obiettivo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
